import { useState, useEffect, useRef } from 'react';
import styles from 'styles/components/common/forms/AttachedFile.module.scss';
import { ReactComponent as PlusIcon } from 'assets/icons/plus.svg';
import useModal from 'hooks/useModal';

type TProps = {
  isBoard?: boolean;
  name?: string;
  files?: File[];
  multiple?: boolean;
  maxLength?: number;
  limit?: number;
  inputId: string;
  accept?: string;
  readOnly?: boolean;
  className?: string;
  btnIcon?: string;
  btnText?: string;
  btnStyle?: object;
  listStyle?: object;
  onDelete?: (index: number) => void;
  onChange?: (file: File[]) => void;
};

const FormAttachedFile = (props: TProps) => {
  const acceptExt = props.accept
    ? props.accept
    : `jpg, jpeg, png, pdf, hwp, pptx, docx`;
  const acceptExtArray = acceptExt.split(',').map((ext) => ext.trim());
  const fileLimit = props.limit ? props.limit : 10;
  const fileLength = props.maxLength ? props.maxLength : 1;

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [fileList, setFileList] = useState<File[]>([]);

  const { openModal } = useModal();

  useEffect(() => {
    setFileList(props.files || []);
  }, [props.files]);

  const resetFile = (index?: number) => {
    const dataTransfer = new DataTransfer();

    if (index !== undefined) {
      const fileArray = props.files ? Array.from(props.files) : [];

      fileArray.splice(index, 1);
      fileArray.forEach((file) => dataTransfer.items.add(file));
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
    setFileList(Array.from(dataTransfer.files ?? []));
    props.onChange && props.onChange(Array.from(dataTransfer.files ?? []));
  };

  const deleteFile = (index: number) =>
    openModal({
      isConfirm: true,
      title: '첨부파일 삭제',
      text: '첨부파일을 삭제하시겠습니까?',
      onSubmit: () => {
        const type = fileList[index].type.split(':');

        if (type[0] === 'fileid') {
          props.onDelete && props.onDelete(Number(type[1]));
        }
        resetFile(index);
      },
    });

  const changeFile = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) {
      resetFile();
      return;
    }

    const files = event.target.files;
    const fileNames = Array.from(files).map((file) => file.name);
    const fileSize = Array.from(files).map((file) => file.size / 1024 / 1024);
    const fileExts = fileNames.map(
      (name) => name.split('.').pop()?.toLowerCase() || null
    );

    if (fileList.length + files.length > fileLength) {
      openModal({
        text: `최대 ${fileLength}개까지만 업로드 가능합니다.`,
      });
      return;
    }

    if (fileExts.find((ext) => !acceptExtArray.includes(ext || ''))) {
      openModal({
        text: `${acceptExt} 파일 형식만 업로드 가능합니다.\n (최대 ${fileLength}개, ${fileLimit}MB 이하)`,
      });
      return;
    }

    if (fileSize.find((size) => size > fileLimit)) {
      openModal({
        text: `${fileLimit}MB 이하 파일만 업로드 가능합니다.`,
      });
      return;
    }

    const dataTransfer = new DataTransfer();
    const fileArray = [...fileList, ...Array.from(files)];

    fileArray.forEach((file) => dataTransfer.items.add(file));
    setFileList(Array.from(dataTransfer.files));
    props.onChange && props.onChange(Array.from(dataTransfer.files));
  };

  return (
    <div className={props.className}>
      {props.isBoard && (
        <>
          <dl className={styles.desc}>
            <dt>첨부파일</dt>
            <dd>
              <p>
                파일은 최대 {fileLength}개까지만 첨부 가능합니다. (최대 용량{' '}
                {fileLimit}MB)
              </p>
              <p className="font_red">※ 지원하는 파일 형식 : {acceptExt}</p>
            </dd>
          </dl>
        </>
      )}
      <input
        type="file"
        ref={fileInputRef}
        name={props.name}
        id={props.inputId}
        multiple={props.multiple || false}
        accept={acceptExt}
        disabled={props.readOnly || false}
        className="hidden"
        onChange={(event) => changeFile(event)}
      />
      {fileList.length >= fileLength || props.readOnly ? (
        <span
          className={styles.btn_disabled}
          style={{
            ...props.btnStyle,
            ...(props.isBoard ? { margin: '24px 0 0 80px' } : {}),
          }}
        >
          {props.btnIcon && props.btnIcon === 'plus' && (
            <PlusIcon width={20} height={20} fill="#fff" />
          )}
          {props.btnText || '파일 업로드'}
        </span>
      ) : (
        <label
          htmlFor={props.inputId}
          className={styles.btn}
          style={{
            ...props.btnStyle,
            ...(props.isBoard ? { margin: '24px 0 0 80px' } : {}),
          }}
        >
          {props.btnIcon && props.btnIcon === 'plus' && (
            <PlusIcon width={20} height={20} fill="#fff" />
          )}
          {props.btnText || '파일 업로드'}
        </label>
      )}

      {fileList.length > 0 && (
        <ul className={styles.file_list} style={{ ...props.listStyle }}>
          {fileList.map((file, index) => (
            <li key={index} className={styles.file}>
              <span>{file.name}</span>
              {!props.readOnly && (
                <button
                  type="button"
                  className={styles.delete}
                  onClick={() => deleteFile(index)}
                >
                  삭제
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default FormAttachedFile;
